import React, { useEffect, useMemo, useState } from "react";
import api, { inr } from "../../lib/api";
import { toast } from "sonner";
import { Search, Package, AlertTriangle, XCircle, TrendingUp, Boxes, Plus, Minus, History, X, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";

const STATUS_META = {
  in_stock: { label: "In Stock", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  low_stock: { label: "Low Stock", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  out_of_stock: { label: "Out of Stock", pill: "bg-rose-50 text-rose-700 border-rose-200" },
};

const ADJUST_TYPES = [
  { value: "receive", label: "Receive stock (+)" },
  { value: "adjust", label: "Manual adjustment" },
  { value: "return", label: "Customer return (+)" },
  { value: "write_off", label: "Write off (-)" },
];

const HISTORY_TYPE_META = {
  receive: { label: "Receive", cls: "text-emerald-600" },
  adjust: { label: "Adjust", cls: "text-welljio-accent" },
  return: { label: "Return", cls: "text-sky-600" },
  write_off: { label: "Write Off", cls: "text-rose-600" },
  sale: { label: "Sale", cls: "text-welljio-muted" },
};

const StatCard = ({ icon: Icon, label, value, tone = "default", testid }) => {
  const tones = {
    default: "bg-white",
    good: "bg-emerald-50",
    warn: "bg-amber-50",
    bad: "bg-rose-50",
  };
  const iconTones = {
    default: "text-welljio-accent",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-rose-600",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border border-slate-100 p-5 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.2em] text-welljio-subtle font-semibold">{label}</p>
        <Icon size={18} className={iconTones[tone]} strokeWidth={2.2} />
      </div>
      <p className="font-heading text-3xl font-bold tracking-tight mt-3 text-welljio-ink">{value}</p>
    </div>
  );
};

export default function AdminInventoryPage() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [adjustFor, setAdjustFor] = useState(null);       // product being adjusted
  const [historyFor, setHistoryFor] = useState(null);     // product whose history is shown
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/inventory"),
        api.get("/admin/inventory/stats"),
      ]);
      setProducts(list);
      setStats(st);
      const cats = Array.from(new Set(list.map((p) => p.category).filter(Boolean))).map((name) => ({ name, slug: list.find((p) => p.category === name)?.category_slug }));
      setCategories(cats);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Client-side search + filters (backend also supports it, but local is snappier)
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (category && p.category_slug !== category) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (q) {
        const hay = [p.name, p.sku, p.category].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, search, category, statusFilter]);

  const openHistory = async (product) => {
    setHistoryFor(product);
    setHistoryEvents([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/inventory/${product.id}/history`);
      setHistoryEvents(data);
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  };

  const handleAdjusted = (updated) => {
    setProducts((prev) => prev.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
    // Refresh stats after any change
    api.get("/admin/inventory/stats").then((r) => setStats(r.data)).catch(() => {});
    if (historyFor && historyFor.id === updated.id) {
      // refresh history too
      openHistory(updated);
    }
  };

  return (
    <div data-testid="admin-inventory-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Manage</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Inventory</h1>
          <p className="text-welljio-muted mt-1 text-sm">Track stock, log movements and get low-stock alerts.</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="inventory-stats">
          <StatCard testid="stat-total-skus" icon={Boxes} label="Total SKUs" value={stats.total_skus} />
          <StatCard testid="stat-in-stock" icon={Package} label="In Stock" value={stats.in_stock} tone="good" />
          <StatCard testid="stat-low-stock" icon={AlertTriangle} label="Low Stock" value={stats.low_stock} tone="warn" />
          <StatCard testid="stat-out-of-stock" icon={XCircle} label="Out of Stock" value={stats.out_of_stock} tone="bad" />
          {stats.stock_value !== undefined && (
           <StatCard testid="stat-stock-value" icon={TrendingUp} label="Stock Value" value={inr(stats.stock_value)} />
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-welljio-muted pointer-events-none" strokeWidth={2} />
          <input
            type="search"
            data-testid="inventory-search"
            placeholder="Search product, SKU, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-full border border-slate-200 pl-10 pr-4 py-2.5 text-sm bg-white w-72 max-w-full focus:outline-none focus:ring-2 focus:ring-welljio-accent/30 focus:border-welljio-accent transition-colors"
          />
        </div>
        <select
          data-testid="inventory-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-full border border-slate-200 px-5 py-2.5 text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="in_stock">In stock</option>
          <option value="low_stock">Low stock</option>
          <option value="out_of_stock">Out of stock</option>
        </select>
        <select
          data-testid="inventory-category-filter"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-full border border-slate-200 px-5 py-2.5 text-sm bg-white"
        >
          <option value="">All categories</option>
          {categories.map((c) => c.slug && <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        {(search || statusFilter || category) && (
          <button
            type="button"
            data-testid="inventory-reset-filters"
            onClick={() => { setSearch(""); setStatusFilter(""); setCategory(""); }}
            className="inline-flex items-center gap-1.5 text-xs text-welljio-muted hover:text-welljio-ink transition-colors"
          >
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-welljio-muted">Loading inventory…</p>
      ) : visible.length === 0 ? (
        <p className="text-welljio-muted" data-testid="inventory-empty">No products match the current filters.</p>
      ) : (
        <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-welljio-tint">
                <tr className="text-left">
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Product</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">SKU</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Category</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap text-right">Stock</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap text-right">Threshold</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Status</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.in_stock;
                  return (
                    <tr key={p.id} data-testid={`inventory-row-${p.id}`} className="border-t border-slate-100 hover:bg-welljio-tint/40 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-welljio-tint border border-slate-100 overflow-hidden shrink-0">
                            {(p.images || [])[0] && <img src={p.images[0]} alt="" className="w-full h-full object-contain p-1" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{p.name}</p>
                            {p.price !== undefined && <p className="text-xs text-welljio-muted">{inr(p.price)}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">{p.sku}</td>
                      <td className="px-5 py-3 text-welljio-muted whitespace-nowrap">{p.category}</td>
                      <td className="px-5 py-3 font-semibold tabular-nums text-right">{p.stock}</td>
                      <td className="px-5 py-3 tabular-nums text-right text-welljio-muted">{p.low_stock_threshold}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide ${meta.pill}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            data-testid={`inventory-adjust-${p.id}`}
                            onClick={() => setAdjustFor(p)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs hover:bg-welljio-tint hover:border-welljio-accent/40 transition-colors"
                          >
                            <Plus size={12} strokeWidth={2.4} /> Adjust
                          </button>
                          <button
                            type="button"
                            data-testid={`inventory-history-${p.id}`}
                            onClick={() => openHistory(p)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs hover:bg-welljio-tint hover:border-welljio-accent/40 transition-colors"
                          >
                            <History size={12} strokeWidth={2.4} /> History
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adjust modal */}
      {adjustFor && (
        <AdjustModal
          product={adjustFor}
          onClose={() => setAdjustFor(null)}
          onAdjusted={(updated) => { handleAdjusted(updated); setAdjustFor(null); }}
        />
      )}

      {/* History sheet */}
      <Sheet open={!!historyFor} onOpenChange={(o) => { if (!o) setHistoryFor(null); }}>
        <SheetContent side="right" data-testid="inventory-history-sheet" className="w-full sm:max-w-lg overflow-y-auto bg-white p-0">
          <SheetHeader className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-slate-100 px-6 py-5">
            <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle mb-1">Inventory History</p>
            <SheetTitle className="font-heading text-2xl font-bold tracking-tight text-welljio-ink">
              {historyFor?.name}
            </SheetTitle>
            <p className="text-xs text-welljio-muted mt-1">SKU: <span className="font-mono">{historyFor?.sku}</span> · Current stock: <b>{historyFor?.stock}</b></p>
          </SheetHeader>
          <div className="px-6 py-5">
            {historyLoading ? (
              <p className="text-welljio-muted text-sm">Loading history…</p>
            ) : historyEvents.length === 0 ? (
              <p className="text-welljio-muted text-sm">No movements recorded yet.</p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {historyEvents.map((e) => (
                  <li key={e.id} className="ml-4">
                    <span className="absolute -left-1.5 flex items-center justify-center w-3 h-3 rounded-full bg-welljio-accent ring-4 ring-welljio-tint/60" />
                    <div className="flex items-center justify-between gap-3">
                      <p className={`text-sm font-semibold ${HISTORY_TYPE_META[e.type]?.cls || "text-welljio-ink"}`}>
                        {HISTORY_TYPE_META[e.type]?.label || e.type}
                        <span className="ml-2 text-xs font-mono">{e.delta > 0 ? `+${e.delta}` : e.delta}</span>
                      </p>
                      <p className="text-xs text-welljio-muted tabular-nums">{e.stock_before} → {e.stock_after}</p>
                    </div>
                    {e.reason && <p className="text-xs text-welljio-ink mt-0.5">{e.reason}</p>}
                    <p className="text-[11px] text-welljio-muted mt-0.5">
                      {new Date(e.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {e.actor_email}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function AdjustModal({ product, onClose, onAdjusted }) {
  const [type, setType] = useState("receive");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState(product.low_stock_threshold);
  const isDecrement = type === "write_off";
  const signedDelta = isDecrement ? -Math.abs(qty) : Math.abs(qty);
  const nextStock = Math.max(0, product.stock + signedDelta);

  const save = async () => {
    if (qty <= 0) return toast.error("Quantity must be greater than 0");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/inventory/${product.id}/adjust`, { delta: signedDelta, type, reason });
      if (threshold !== product.low_stock_threshold) {
        await api.patch(`/admin/inventory/${product.id}/threshold`, { low_stock_threshold: Number(threshold) });
        data.low_stock_threshold = Number(threshold);
      }
      toast.success(`${product.name}: stock updated to ${data.stock}`);
      onAdjusted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adjust stock");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-welljio-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="inventory-adjust-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle font-semibold">Update Stock</p>
            <h2 className="font-heading text-lg font-bold text-welljio-ink truncate">{product.name}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-4 text-xs bg-welljio-tint rounded-2xl p-3">
            <div>
              <p className="text-welljio-subtle uppercase tracking-wide">Current</p>
              <p className="text-2xl font-bold font-heading">{product.stock}</p>
            </div>
            <div className="text-welljio-muted">→</div>
            <div>
              <p className="text-welljio-subtle uppercase tracking-wide">After</p>
              <p className="text-2xl font-bold font-heading text-welljio-accent" data-testid="adjust-next-stock">{nextStock}</p>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-welljio-subtle font-semibold">Type</label>
            <select
              data-testid="adjust-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white"
            >
              {ADJUST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-welljio-subtle font-semibold">Quantity</label>
            <div className="mt-1.5 flex items-center gap-2">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center">
                <Minus size={14} />
              </button>
              <input
                data-testid="adjust-qty"
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-center font-medium tabular-nums"
              />
              <button type="button" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center">
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-welljio-subtle font-semibold">Reason (optional)</label>
            <input
              data-testid="adjust-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Weekly supplier delivery"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-welljio-subtle font-semibold">Low-stock threshold</label>
            <input
              data-testid="adjust-threshold"
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            />
          </div>
        </div>
        <div className="px-6 py-4 bg-welljio-tint/40 border-t border-slate-100 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-welljio-muted hover:text-welljio-ink">Cancel</button>
          <button
            type="button"
            data-testid="adjust-save"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-welljio-accent text-white px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
